import React, { useState, useEffect, useCallback } from 'react';
import { ordersAPI } from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import {
  MapIcon,
  TruckIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const TrackOrders: React.FC = () => {
  const { formatPrice } = useCurrency();
  const [activeOrders, setActiveOrders] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [showRating, setShowRating] = useState(false);

  const fetchActiveOrders = useCallback(async () => {
    try {
      const allOrders = await ordersAPI.getUserOrders();
      
      // Show all successfully created orders (exclude failed payments and cancelled)
      const successfulOrders = Array.isArray(allOrders) ? allOrders.filter((order: any) => 
        ['pending', 'accepted', 'assigned', 'picked_up', 'in_transit', 'delivered'].includes(order.status)
      ) : [];
      
      setActiveOrders(successfulOrders);
      
      // Update selected order with fresh data
      setSelectedOrder((prev: any) => {
        if (prev) {
          const updatedSelectedOrder = successfulOrders.find((order: any) => order.id === prev.id);
          if (updatedSelectedOrder) {
            return updatedSelectedOrder;
          } else {
            // Order not found, select first one if available
            return successfulOrders.length > 0 ? successfulOrders[0] : null;
          }
        } else if (successfulOrders.length > 0) {
          // No order selected, auto-select first one
          return successfulOrders[0];
        }
        return null;
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveOrders();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchActiveOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchActiveOrders]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'accepted':
      case 'assigned':
        return 'bg-volexx-blue-100 text-volexx-blue-800 border-volexx-blue-200';
      case 'picked_up':
      case 'in_transit':
        return 'bg-volexx-orange-100 text-volexx-orange-800 border-volexx-orange-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const calculateETA = (order: any) => {
    if (order.total_duration_minutes) {
      return `${order.total_duration_minutes} min`;
    }
    return 'Calculating...';
  };

  const handleRateDelivery = async (orderId: string, ratingValue: number) => {
    // TODO: Implement rating API call
    console.log(`Rating order ${orderId} with ${ratingValue} stars`);
    setShowRating(false);
    setRating(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-4 border-gray-200 border-t-volexx-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Track Your Orders</h1>
          <p className="mt-2 text-gray-600">View all your deliveries and track their real-time status</p>
        </div>

        {activeOrders.length === 0 ? (
          <div className="bg-white rounded-volexx-lg shadow-volexx-md p-12 text-center">
            <TruckIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
            <p className="text-gray-500">You haven't created any orders yet. Create your first order to get started!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Orders List */}
            <div className="lg:col-span-1 space-y-4">
              {activeOrders.map((order) => (
                <div
                  key={order.id}
                  onClick={() => setSelectedOrder(order)}
                  className={`bg-white rounded-volexx-lg shadow-volexx-md p-4 cursor-pointer transition-all ${
                    selectedOrder?.id === order.id ? 'ring-2 ring-volexx-blue-500' : 'hover:shadow-volexx-lg'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-900">
                      Order #{order.id?.toString().slice(-8) || order.id}
                    </span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5 mr-2"></div>
                      <p className="text-xs text-gray-600 flex-1">
                        {order.stops && order.stops.length > 0 ? order.stops[0]?.address : 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <div className="h-2 w-2 rounded-full bg-red-500 mt-1.5 mr-2"></div>
                      <p className="text-xs text-gray-600 flex-1">
                        {order.stops && order.stops.length > 1 ? order.stops[order.stops.length - 1]?.address : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      ETA: {calculateETA(order)}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {formatPrice(order.final_price || 0)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Map and Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Order Details */}
              {selectedOrder && (
                <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Order Details - #{selectedOrder.id?.toString().slice(-8) || selectedOrder.id}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700">Status</label>
                      <div className="mt-1">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(selectedOrder.status)}`}>
                          {getStatusText(selectedOrder.status)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-700">Package Details</label>
                      <p className="mt-1 text-sm text-gray-600">
                        {selectedOrder.package_description || 'No description'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Weight: {selectedOrder.package_weight_kg || 0}kg
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-semibold text-gray-700">Distance</label>
                        <p className="mt-1 text-sm text-gray-600">
                          {selectedOrder.total_distance_km?.toFixed(1)} km
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700">ETA</label>
                        <p className="mt-1 text-sm text-gray-600">
                          {calculateETA(selectedOrder)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-700">Total Price</label>
                      <p className="mt-1 text-2xl font-bold text-volexx-blue-600">
                        {formatPrice(selectedOrder.final_price || 0)}
                      </p>
                    </div>

                    {selectedOrder.status === 'delivered' && (
                      <div className="border-t pt-4">
                        <button
                          onClick={() => setShowRating(true)}
                          className="btn-primary w-full"
                        >
                          Rate This Delivery
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rating Modal */}
              {showRating && selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-volexx-lg shadow-volexx-xl p-6 max-w-md w-full">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Rate Your Delivery
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      How was your experience with this delivery?
                    </p>
                    <div className="flex justify-center space-x-2 mb-6">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          className="focus:outline-none transform hover:scale-110 transition-transform"
                        >
                          {star <= rating ? (
                            <StarIconSolid className="h-10 w-10 text-yellow-400" />
                          ) : (
                            <StarIcon className="h-10 w-10 text-gray-300" />
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowRating(false);
                          setRating(0);
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-volexx font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleRateDelivery(selectedOrder.id, rating)}
                        disabled={rating === 0}
                        className="flex-1 btn-primary disabled:opacity-50"
                      >
                        Submit Rating
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrders;
